import CloseIcon from "@mui/icons-material/Close";
import { Alert, Backdrop, Button, IconButton, SelectChangeEvent } from "@mui/material";
import { css, StyleSheet } from "aphrodite";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { OnDragEndResponder } from "react-beautiful-dnd";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import App, { IFormDialog } from "src/pi/application/App";
import { DraggableFormCellItem } from "./FormCell/DragAndDrop/DraggableFormCell";
import { Row } from "./FormCell/DragAndDrop/DraggableItemTypes";
import { HiddenFieldSidebar } from "./FormCell/DragAndDrop/HiddenFieldSidebar";
import { CustomizeFormLayout } from "./FormLayout/CustomizeFormLayout";
import { BreakpointSelector } from "./BreakpointSelector";
import * as auto_pi from "@crochik/auto-pi";
import { useForm } from "./useForm";
import { toJS } from "mobx";
import { parseResponseError } from "src/pi/api/Client";
import { Action } from "src/pi/context/IForm";
import { IDataFormActionResponse } from "src/pi/services/DataService";

interface Props {
    isVisible?: (field: auto_pi.Field) => boolean;
    onRender?(field: auto_pi.Field): ReactNode;
    onClose?(layouts: { [key: string]: auto_pi.FormLayout }): void;
}

const toolbarHeight = 80;
const sidebarWidth = 360;
const formPadding = 32;

interface DeviceSize {
    breakpoint: auto_pi.FormLayoutBreakpointEnum;
    maxWidth: number;
    label: string;
}

const deviceSizes: DeviceSize[] = [
    { breakpoint: auto_pi.FormLayoutBreakpointEnum.Medium, maxWidth: 1200, label: "Desktop (Md<1200)" },
    { breakpoint: auto_pi.FormLayoutBreakpointEnum.Small, maxWidth: 900, label: "Tablet (Sm<900)" },
    { breakpoint: auto_pi.FormLayoutBreakpointEnum.ExtraSmall, maxWidth: 600, label: "Phone (Xs<600)" }
];

type OnFormAction = (dialog: IFormDialog, action?: Action) => boolean;

export function FormBuilder(props: Props) {
    const { isVisible, onRender, onClose } = props;
    const form = useForm();
    const screenWidth = App().innerWidth;

    const [isLoading, setLoading] = useState(false);
    const [deviceSize, setDeviceSize] = useState(getInitialDeviceSize());
    const [layouts, setLayouts] = useState<{ [key: string]: auto_pi.FormLayout }>(form.Layouts || {});
    const [rows, setRows] = useState<Row[]>([]);
    const [hiddenFields, setHiddenFields] = useState<auto_pi.Field[]>([]);


    const dataForm = (path: string, onAction: OnFormAction, body?: object) => {
        setLoading(true);

        App()
            .dataFormAsync(path, undefined, async (dialog: IFormDialog, action?: Action, lastResult?: IDataFormActionResponse) => {
                setLoading(true);
                if (onAction(dialog, action)) return true;

                if (action?.name === "#close") {
                    // let caller handle to avoid stackoverflow 
                    setLoading(false);
                    return false;
                }

                // trigger close 
                App().closeDataFormDialogAsync(true);
                return true;
            }, undefined, body)
            .catch(e => {
                parseResponseError(e).then(error => alert(error));
                setLoading(false);
            });
    };

    const onAddField = (type: auto_pi.FieldTEnum) => {
        dataForm(
            `/api/v1/FormDesigner/Field(${type})/Add`,
            (dialog, action) => {
                if ((action?.action ?? action?.name) === "Add") {
                    const payload = toJS(dialog?.form?.values) as auto_pi.Field;
                    form?.Fields?.push(payload);
                    setHiddenFields([...hiddenFields, payload])
                }

                return false;
            }
        );
    }

    const onEditField = (field?: auto_pi.Field) => {
        if (field) {
            dataForm(
                `/api/v1/FormDesigner/Field(${field._t})/Edit`,
                (dialog, action) => {
                    if ((action?.action ?? action?.name) === "Update") {
                        const payload = toJS(dialog?.form?.values) as auto_pi.Field;
                        Object.assign(field, payload);
                    }

                    return false;
                },
                field
            );

        } else {
            // TODO: add field w/o kwowing type
            // ...
        }
    }

    const onEditAction = (formAction?: auto_pi.FormAction) => {
        if (formAction) {
            // edit action
            dataForm(
                `/api/v1/FormDesigner/FormAction/Edit`,
                (dialog, action) => {
                    if ((action?.action ?? action?.name) === "Update") {
                        const payload = toJS(dialog?.form?.values) as auto_pi.Field;
                        Object.assign(formAction, payload);
                    }

                    return false;
                },
                formAction
            );
        } else {
            // add action
            dataForm(
                `/api/v1/FormDesigner/FormAction/Add`,
                (dialog, action) => {
                    if ((action?.action ?? action?.name) === "Add") {
                        const payload = toJS(dialog?.form?.values) as auto_pi.FormAction;
                        form.Actions ??= [];
                        form.Actions.push(payload);
                    }

                    return false;
                }
            );
        }
    }

    const onFormProperties = () => {
        // form properties
        dataForm(
            `/api/v1/FormDesigner/Form/Edit`,
            (dialog, action) => {
                if ((action?.action ?? action?.name) === "Update") {
                    const payload = toJS(dialog?.form?.values) as auto_pi.Form;
                    delete payload.Actions;
                    delete payload.Fields;
                    delete payload.Layouts;
                    Object.assign(form, payload);
                }

                return false;
            },
            {
                ...form,
                Actions: undefined,
                Fields: undefined,
                Layouts: undefined,
            }
        );
    }

    const initFromLayout = useCallback(
        (anyLayout: auto_pi.FormLayout) => {
            if (anyLayout._t !== auto_pi.FormLayoutTEnum.GridFormLayout) {
                console.error("only grid layouts supported");
                return;
            }
            const layout = anyLayout as auto_pi.GridFormLayout;
            const visible: { [name: string]: boolean } = {};

            const newRows: Row[] = layout?.Rows?.map((row) => {
                const fullFields = row.Fields
                    .map((field) => {
                        return form.Fields?.find((x) => x.Name === field.Name);
                    })
                    .filter((field) => {
                        return !!field; // && field._t !== auto_pi.FieldTEnum.HiddenField;
                    }) ?? [];

                fullFields.forEach(field => {
                    visible[field!.Name] = true;
                });

                return { ...row, fields: fullFields } as Row;
            }).filter(x => x.fields.length > 0) ?? [];

            setRows(newRows);

            const hiddenFields = form?.Fields?.filter(x => !visible[x.Name!])?.sort((a, b) => (a.Label ?? a.Name ?? "").localeCompare(b.Label ?? b.Name ?? "")); // x._t !== auto_pi.FieldTEnum.HiddenField
            setHiddenFields(hiddenFields ?? []);
        },
        [form?.Fields]
    );

    const resetLayout = useCallback(() => {
        const hiddenFields: auto_pi.Field[] = [];
        const newRows = form?.Fields?.filter((field) => {
            if (isVisible && !isVisible(field)) {
                hiddenFields.push(field);
                return false;
            }
            return true; // field._t !== auto_pi.FieldTEnum.HiddenField;
        }).map((x) => {
            const row = {
                fields: [x],
            };
            return row;
        });

        const grid: auto_pi.GridFormLayout = {
            _t: auto_pi.FormLayoutTEnum.GridFormLayout,
            Breakpoint: deviceSize.breakpoint,
            Rows: newRows?.map(x => {
                const row: auto_pi.GridFormRowLayout = {
                    Fields: x.fields.map(x => {
                        return { Name: x.Name, Width: 12 } as auto_pi.GridFormFieldLayout;
                    })
                };
                return row;
            }) ?? []
        };

        console.log('resetLayout', deviceSize, newRows, grid);

        const newLayouts = {
            ...layouts,
            [deviceSize.breakpoint]: grid
        };

        setLayouts(newLayouts);
        setRows(newRows || []);
        setHiddenFields(hiddenFields.sort((a, b) => (a.Label ?? a.Name ?? "").localeCompare(b.Label ?? b.Name ?? "")));
    }, [form?.Fields, deviceSize, layouts, isVisible]);

    useEffect(() => {
        if (!form?.Fields?.length) return;

        const layout = layouts[deviceSize.breakpoint] ?? form?.Layouts?.[deviceSize.breakpoint];
        if (layout) {
            initFromLayout(layout);
        } else {
            resetLayout();
        }

    }, [form.Fields, layouts, deviceSize, initFromLayout]);

    useEffect(() => {
        // form.hash changed (fields changed)
        if (!form?.Fields?.length) return;

        if (form.Layouts && deviceSize.breakpoint in form.Layouts) {
            console.log("load layout");
            setLayouts(form.Layouts);
            const layout = form.Layouts[deviceSize.breakpoint];
            initFromLayout(layout);
        }

    }, [form?.Fields?.length]); // replace with some hash?

    function getInitialDeviceSize() {
        return deviceSizes[0];
    }

    const updateLayout = useCallback((newBreakpoint?: auto_pi.FormLayoutBreakpointEnum) => {
        const newLayout: auto_pi.GridFormLayout = {
            _t: auto_pi.FormLayoutTEnum.GridFormLayout,
            Breakpoint: deviceSize.breakpoint,
            Rows: rows?.map(x => {
                const row: auto_pi.GridFormRowLayout = {
                    Fields: x.fields.map(x => {
                        return { Name: x.Name, Width: 12 } as auto_pi.GridFormFieldLayout;
                    })
                };
                return row;
            }) ?? []
        };

        let newLayouts: { [key: string]: auto_pi.FormLayout } = { ...layouts, [deviceSize.breakpoint]: newLayout };
        if (!!newBreakpoint && !newLayouts[newBreakpoint]) {
            const copy: auto_pi.GridFormLayout = {
                _t: auto_pi.FormLayoutTEnum.GridFormLayout,
                Breakpoint: newBreakpoint,
                Rows: rows?.map(x => {
                    const row: auto_pi.GridFormRowLayout = {
                        Fields: x.fields.map(x => {
                            return { Name: x.Name, Width: 12 } as auto_pi.GridFormFieldLayout;
                        })
                    };
                    return row;
                }) ?? []
            };

            newLayouts[newBreakpoint] = copy;
        }

        setLayouts(newLayouts);
        return newLayouts;

    }, [deviceSize.breakpoint, layouts, rows]);

    const maxWidth = useMemo(() => deviceSize.maxWidth, [deviceSize]);

    const isScreenTooSmall = useMemo(() => {
        return screenWidth - sidebarWidth - formPadding * 2 < maxWidth;
    }, [screenWidth, maxWidth]);

    const onChangeMaxWidth = (event: SelectChangeEvent<number>) => {
        const newDeviceSize = deviceSizes.find((x) => x.maxWidth === event.target.value);
        if (!!newDeviceSize) {
            setDeviceSize(newDeviceSize);
            updateLayout(newDeviceSize.breakpoint);
        }
    };

    const deleteItem = (item: DraggableFormCellItem, tmpRows: Row[]) => {
        if (tmpRows[item.currentRowId].fields.length === 1) {
            tmpRows.splice(item.currentRowId, 1);
        } else {
            const tmpFields = [...tmpRows[item.currentRowId].fields];
            tmpFields.splice(item.currentFieldId, 1);
            tmpRows[item.currentRowId].fields = tmpFields;
        }
        return tmpRows;
    };

    const reorder = (list: any[], startIndex: number, endIndex: number) => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);

        return result;
    };

    const onRemoveFromLayout = (item: DraggableFormCellItem) => {
        setRows((prevRows) => {
            let tmpRows = [...prevRows];
            tmpRows = deleteItem(item, tmpRows);
            return tmpRows;
        });

        setHiddenFields((prevHiddenFields) => {
            let tmpHiddenFields = [...prevHiddenFields];
            tmpHiddenFields.push(item.field);
            return tmpHiddenFields.sort((a, b) => (a.Label ?? a.Name ?? "").localeCompare(b.Label ?? b.Name ?? ""));
        });
    };

    const removeHiddenField = (item: DraggableFormCellItem) => {
        setHiddenFields((prevHiddenFields) => {
            let tmpHiddenFields = [...prevHiddenFields];
            const index = tmpHiddenFields.indexOf(item.field);
            if (index >= 0) {
                tmpHiddenFields.splice(index, 1);
            }
            return tmpHiddenFields;
        });
    };

    const onDropExisting = useCallback(
        (item: DraggableFormCellItem) => {
            console.log(`Move field to exisiting row: (${item.currentRowId}/${item.currentFieldId}) -> (${item.newRowId}/${item.newFieldId})`);
            setRows((prevRows) => {
                let tmpRows = [...prevRows];
                // Add field to existing row in correct place
                if (item.newFieldId !== undefined && item.newRowId !== undefined) {
                    // reorder fields
                    if (item.currentRowId === item.newRowId && !item.hidden) {
                        const rowId = item.currentRowId;
                        const fields = prevRows[rowId].fields;
                        const reorderedFields = reorder(fields, item.currentFieldId, item.newFieldId);
                        tmpRows[rowId].fields = reorderedFields;
                    } else {
                        const addedFields = [...prevRows[item.newRowId].fields];
                        addedFields.splice(item.newFieldId, 0, item.field);
                        tmpRows[item.newRowId].fields = addedFields;
                        if (!item.hidden) {
                            tmpRows = deleteItem(item, tmpRows);
                        }
                    }
                }
                return tmpRows;
            });

            if (item.hidden) {
                removeHiddenField(item);
            }
        },
        [setRows]
    );

    const onDropNew = useCallback(
        (item: DraggableFormCellItem) => {
            console.log(`Move field to new row: (${item.currentRowId}/${item.currentFieldId}) -> (${item.newRowId}/${item.newFieldId || 0})`);
            setRows((prevRows) => {
                let tmpRows = [...prevRows];
                if (item.newRowId !== undefined) {
                    tmpRows.splice(item.newRowId + 1, 0, { fields: [item.field] });
                    if (!item.hidden) {
                        const itemToDelete: DraggableFormCellItem =
                            item.newRowId >= item.currentRowId ? item : {
                                ...item,
                                currentRowId: item.currentRowId + 1
                            };
                        tmpRows = deleteItem(itemToDelete, tmpRows);
                    }
                }
                return tmpRows;
            });
            if (item.hidden) {
                removeHiddenField(item);
            }
        },
        [setRows]
    );

    const onReorder: OnDragEndResponder = (result) => {
        if (!result.destination) {
            return;
        }

        setRows((prevRows) => {
            let tmpRows = [...prevRows];
            if (!!result?.destination) {
                tmpRows = reorder(tmpRows, result.source.index, result.destination.index);
            }
            return tmpRows;
        });
    };

    const onClearLayout = (size: string) => (event: React.MouseEvent) => {
        event.stopPropagation();

        const newLayouts = { ...layouts };
        delete newLayouts[size];

        setLayouts(newLayouts);
    };

    const onCloseDesigner = useCallback(() => {
        onClose?.(updateLayout());
    }, [onClose, updateLayout]);

    return (
        <>
            <DndProvider backend={HTML5Backend}>
                <div className={css(styles.container)}>
                    <div className={css(styles.toolbar)}>
                        <div style={{ display: "flex", alignItems: "center" }}>
                            <BreakpointSelector value={maxWidth} onChange={onChangeMaxWidth} layouts={layouts}
                                onClear={onClearLayout} />
                            <Button sx={{ height: 45, marginLeft: 1 }} variant="outlined"
                                onClick={resetLayout}>Reset</Button>
                        </div>
                        <div className={css(styles.title)}>{form.Name}</div>
                        <div style={{ display: "flex", alignItems: "center" }}>
                            {/* {form.url && <Button sx={{ height: 45, marginLeft: 1 }} variant="outlined"
                                onClick={saveLayoutClick}>Save</Button>} */}
                            {onClose && (
                                <IconButton onClick={onCloseDesigner}>
                                    <CloseIcon />
                                </IconButton>
                            )}
                        </div>
                    </div>

                    <div className={css(styles.row)}>
                        <div className={css(styles.hiddenFields)}>
                            <HiddenFieldSidebar
                                fields={hiddenFields}
                                form={form}
                                onDrop={onRemoveFromLayout}
                                onProperties={onEditField}
                                width={sidebarWidth}
                                onRender={onRender}
                                onAdd={onAddField}
                            />
                        </div>
                        <div className={css(styles.content)}>
                            <CustomizeFormLayout
                                maxWidth={maxWidth}
                                onDropExisting={onDropExisting}
                                onDropNew={onDropNew}
                                onReorder={onReorder}
                                rows={rows}
                                formPadding={formPadding}
                                isScreenTooSmall={isScreenTooSmall}
                                onRender={onRender}
                                onEditField={onEditField}
                                onEditAction={onEditAction}
                                onFormProperties={onFormProperties}
                            />
                        </div>
                    </div>
                </div>
            </DndProvider>
            {isLoading &&
                <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={isLoading} />}
        </>
    );
}

export const styles = StyleSheet.create({
    container: {
        height: "100%",
        width: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        position: "relative"
    },
    content: {
        backgroundColor: "#eee",
        display: "flex",
        justifyContent: "center",
        height: `100%`,
        width: `calc(100% - ${sidebarWidth}px)`
    },
    hiddenFields: {
        height: `100%`,
        borderRight: "1px solid #ccc"
    },
    row: {
        display: "flex",
        flexGrow: 1,
        height: `calc(100% - ${toolbarHeight}px)`,
        position: "relative"
    },
    toolbar: {
        justifyContent: "space-between",
        padding: 16,
        display: "flex",
        borderBottomStyle: "solid",
        borderBottomWidth: 1,
        borderBottomColor: "#ccc",
        backgroundColor: "white",
        height: toolbarHeight
    },
    title: {
        margin: 0,
        fontSize: 22
    }
});
